import React from 'react'
import * as THREE from 'three'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { FenceElement, FenceSystem } from '../contexts/FenceContext'

interface PDFGeneratorProps {
  elements: FenceElement[]
  selectedSystem: FenceSystem | null
  selectedColor: string | null
  totalLength: number
  totalPrice: number
}

interface PDFGeneratorReturn {
  generateOfferFile: () => Promise<void>
  showClientDataPopup: () => void
  isPopupVisible: boolean
  clientData: {
    name: string
    email: string
    phone: string
    address: string
  }
  setClientData: (data: any) => void
  closePopup: () => void
}

export const usePDFGenerator = ({
  elements,
  selectedSystem,
  selectedColor,
  totalLength,
  totalPrice
}: PDFGeneratorProps): PDFGeneratorReturn => {
  
  const [isPopupVisible, setIsPopupVisible] = React.useState(false)
  const [clientData, setClientData] = React.useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  })
  
  const showClientDataPopup = () => {
    setIsPopupVisible(true)
  }
  
  const closePopup = () => {
    setIsPopupVisible(false)
  }
  
  const generateOfferFile = async (): Promise<void> => {
    try {
      // Oblicz rzeczywistą długość na podstawie wymiarów bloczka
      let calculatedLength = totalLength
      if (selectedSystem?.dimensions) {
        const dimensions = selectedSystem.dimensions.split('×')
        const blockLengthCm = parseInt(dimensions[0]) || 100
        const blockLengthM = blockLengthCm / 100
        
        calculatedLength = elements.reduce((sum, element) => {
          if (element.type === 'post') {
            return sum + blockLengthM
          } else if (element.type === 'section') {
            const multiplier = element.widthMultiplier || 1
            return sum + (blockLengthM * multiplier)
          }
          return sum
        }, 0)
      }


      // Oblicz wymiary wyświetlane dla elementów
      const safeNumber = (value?: number, fallback = 0): number =>
        typeof value === 'number' && !isNaN(value) ? value : fallback

      const formatDimension = (value?: number, fallback = 0): string =>
        `${safeNumber(value, fallback).toFixed(2)}m`

      const getElementWidth = (element: FenceElement) => {
        if (element.type === 'post') {
          // Logic from QuotePreview getPostBaseLength
          const isRoyalMix = selectedSystem?.id?.startsWith('royal') || selectedSystem?.id === 'royal-mix'
          if (selectedSystem?.id === 'lupane-muro-p22' || selectedSystem?.id === 'lupane-muro-s22') return 0.22
          if (selectedSystem?.id === 'lupane-muro-p38' || selectedSystem?.id === 'lupane-muro-s38') return 0.38
          if (isRoyalMix && element?.variant) {
            if (element.variant === 's50' || element.variant === 'p50') return 0.5
          }
          if (selectedSystem?.dimensions && !selectedSystem.dimensions.includes('Wieloformatowe')) {
            const dimensions = selectedSystem.dimensions.split(/[×x]/)
            const blockWidthCm = parseInt(dimensions[0]) || 100
            return blockWidthCm / 100
          }
          return 0.4
        }
        
        if (element.type === 'section') {
          const isMuroMix = selectedSystem?.id?.startsWith('lupane-muro')
          const isRoyalMix = selectedSystem?.id?.startsWith('royal') || selectedSystem?.id === 'royal-mix'
          const variant = element.variant || (selectedSystem?.id === 'lupane-muro-p38' ? 'p38' : 
                                              selectedSystem?.id === 'lupane-muro-p22' ? 'p22' :
                                              selectedSystem?.id === 'lupane-muro-s38' ? 's38' :
                                              selectedSystem?.id === 'lupane-muro-s22' ? 's22' :
                                              selectedSystem?.id === 'royal_s50' ? 's50' :
                                              selectedSystem?.id === 'royal_p50' ? 'p50' : undefined)
          let blockWidth = 0.396
          if (isMuroMix && variant) {
            blockWidth = variant === 's22' ? 0.22 : 0.38
          } else if (isRoyalMix && variant) {
            blockWidth = (variant === 's50' || variant === 'p50') ? 0.5 : 0.5
          } else if (selectedSystem?.dimensions && !selectedSystem.dimensions.includes('Wieloformatowe')) {
            const dimensions = selectedSystem.dimensions.split(/[×x]/)
            const blockWidthCm = parseInt(dimensions[0]) || 100
            blockWidth = blockWidthCm / 100
          }
          
          const widthMultiplier = element.widthMultiplier || 1
          return blockWidth * widthMultiplier
        }
        return element.width || 0
      }

      const getElementHeight = (element: FenceElement) => {
        let blockHeightCm = 20
        if (selectedSystem?.blockHeight) {
          blockHeightCm = selectedSystem.blockHeight
        } else if (selectedSystem?.dimensions && !selectedSystem.dimensions.includes('Wieloformatowe')) {
          const dimensions = selectedSystem.dimensions.split(/[×x]/)
          if (dimensions.length >= 3) {
            blockHeightCm = parseInt(dimensions[2]) || 20
          }
        }
        const blockHeightM = blockHeightCm / 100
        
        let decorationBlockHeightCm = blockHeightCm
        // To precisely match QuotePreview we would need fenceConfig, but without it we can estimate:
        if (selectedSystem?.id === 'roma-horizon' && element.type === 'post') {
            // Usually decoration blocks are same height unless overridden
        }
        const decorationBlockHeightM = decorationBlockHeightCm / 100
        const totalBlocks = typeof element.height === 'number' && element.height > 0 ? element.height : 1
        
        // Default assuming 1 decoration block (daszek)
        if (element.type === 'post' || element.type === 'section') {
          const decorative = Math.min(1, totalBlocks)
          const regular = Math.max(totalBlocks - decorative, 0)
          return (regular * blockHeightM) + (decorative * decorationBlockHeightM)
        }
        
        return element.height || 0
      }

      const getElementDimensions = (element: FenceElement) => {
        if (element.type === 'post' || element.type === 'section') {
          const width = getElementWidth(element)
          const height = getElementHeight(element)
          return `${formatDimension(width)} × ${formatDimension(height)}`
        }
        
        const height = safeNumber(element.height, 1)
        const width = safeNumber(element.width, 1)
        return `${formatDimension(width)} × ${formatDimension(height)}`
      }

      // Nazwy typów elementów
      const getElementTypeName = (type: string) => {
        const typeNames = {
          'post': 'Słupek',
          'section': 'Przęsło',
          'gate': 'Brama',
          'door': 'Furtka'
        }
        return typeNames[type as keyof typeof typeNames] || type
      }

      // Generuj screenshot widoku z góry - automatycznie renderuj z sceny 3D
      let topViewImage = ''
      try {
        // Pobierz scenę Three.js
        const scene = (window as any).getThreeJSScene?.() as THREE.Scene | null
        
        if (scene) {
          console.log('📸 PDF: Renderuję widok z góry z sceny Three.js')
          
          // Oblicz bounding box elementów
          const box = new THREE.Box3()
          scene.traverse((child) => {
            if (child instanceof THREE.Mesh || child instanceof THREE.Group) {
              box.expandByObject(child)
            }
          })
          
          const center = box.getCenter(new THREE.Vector3())
          const size = box.getSize(new THREE.Vector3())
          const maxDim = Math.max(size.x, size.z) || 20
          const margin = maxDim * 0.5
          const frustumSize = maxDim + margin
          const aspect = 600 / 400
          
          // Stwórz kamerę orthographic z góry (identycznie jak w Canvas2D)
          const camera = new THREE.OrthographicCamera(
            -frustumSize * aspect, frustumSize * aspect,
            frustumSize, -frustumSize,
            0.1, 1000
          )
          camera.position.set(center.x, 50, center.z)
          camera.up.set(0, 0, -1) // 🎯 Ważne: ustaw up vector żeby uniknąć skew
          camera.lookAt(center)
          
          // Stwórz tymczasowy renderer
          const tempRenderer = new THREE.WebGLRenderer({ 
            antialias: true,
            preserveDrawingBuffer: true,
            alpha: false
          })
          tempRenderer.setSize(600, 400)
          tempRenderer.setClearColor(0xf9fafb, 1)
          
          // Wyrenderuj scenę
          tempRenderer.render(scene, camera)
          
          // Pobierz obraz z canvas
          topViewImage = tempRenderer.domElement.toDataURL('image/jpeg', 0.95)
          
          // Wyczyść renderer
          tempRenderer.dispose()
        }
      } catch (error) {
        console.error('Błąd podczas generowania screenshotu widoku z góry:', error)
      }
      
      if (!topViewImage) {
        // Fallback - prosty SVG gdy nie ma sceny
        const emptySvg = '<svg width="600" height="400" xmlns="http://www.w3.org/2000/svg"><rect width="600" height="400" fill="#f9fafb"/><text x="300" y="200" text-anchor="middle" font-family="Arial" font-size="16" fill="#6b7280">Brak widoku</text></svg>'
        topViewImage = 'data:image/svg+xml,' + encodeURIComponent(emptySvg)
      }
      
      if (!topViewImage) {
        // Fallback - prosty SVG gdy nie ma kontenera
        const emptySvg = '<svg width="600" height="400" xmlns="http://www.w3.org/2000/svg"><rect width="600" height="400" fill="#f9fafb"/><text x="300" y="200" text-anchor="middle" font-family="Arial" font-size="16" fill="#6b7280">Brak widoku</text></svg>'
        topViewImage = 'data:image/svg+xml,' + encodeURIComponent(emptySvg)
      }

      // Tworzenie HTML
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="pl">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Oferta KOST-BET</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Inter', 'Segoe UI', 'Arial', sans-serif;
              line-height: 1.5;
              color: #1f2937;
              background: #f5f5f5;
              padding: 20px;
              margin: 0;
            }
            
            .container {
              max-width: 100%;
              margin: 0 auto;
              background: #ffffff;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            
            /* Desktop: 70% szerokości ekranu */
            @media (min-width: 768px) {
              body {
                padding: 30px;
              }
              .container {
                max-width: 70%;
              }
            }
            
            /* Mobile: pełna szerokość */
            @media (max-width: 767px) {
              body {
                padding: 10px;
              }
              .container {
                max-width: 100%;
              }
            }
            
            /* Style dla drukowania */
            @media print {
              body {
                background: #ffffff;
                padding: 0;
              }
              
              .container {
                max-width: 100%;
                box-shadow: none;
                margin: 0;
              }
              
              .section {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              
              .header {
                page-break-after: avoid;
              }
              
              .footer {
                page-break-before: avoid;
              }
              
              /* Ukryj elementy niepotrzebne przy druku */
              @page {
                margin: 1.5cm;
                size: A4;
              }
              
              /* Zapewnij czarne tło dla headera w druku */
              .header {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .elements-table th {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .footer {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            .header {
              background: #1f2937;
              color: white;
              padding: 20px;
              margin-bottom: 20px;
              position: relative;
            }
            
            .header h1 {
              font-size: 24px;
              font-weight: 700;
              margin-bottom: 4px;
              letter-spacing: -0.5px;
            }
            
            .header .subtitle {
              font-size: 14px;
              opacity: 0.8;
              font-weight: 400;
            }
            
            .header .date {
              position: absolute;
              top: 20px;
              right: 20px;
              font-size: 12px;
              opacity: 0.8;
              font-weight: 500;
            }
            
            .section {
              background: #ffffff;
              border: 1px solid #e5e7eb;
              padding: 16px;
              margin-bottom: 12px;
            }
            
            .section h2 {
              color: #111827;
              font-size: 16px;
              font-weight: 600;
              margin-bottom: 12px;
              border-bottom: 2px solid #3b82f6;
              padding-bottom: 6px;
            }
            
            .section h3 {
              color: #111827;
              font-size: 14px;
              font-weight: 600;
              margin-bottom: 8px;
            }
            
            
            .fence-details {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }
            
            .fence-details div {
              background: #f8fafc;
              padding: 8px;
              border: 1px solid #e5e7eb;
              font-size: 12px;
            }
            
            .elements-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 8px;
              font-size: 12px;
            }
            
            .elements-table th {
              background: #3b82f6;
              color: white;
              padding: 8px 12px;
              text-align: left;
              font-weight: 600;
              font-size: 12px;
            }
            
            .elements-table td {
              padding: 8px 12px;
              border-bottom: 1px solid #e5e7eb;
              font-size: 12px;
            }
            
            .elements-table tr:nth-child(even) {
              background: #f8fafc;
            }
            
            @media (hover: hover) {
              .elements-table tr:hover {
                background: #f1f5f9;
              }
            }
            
            .summary-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 24px;
              margin-top: 24px;
            }
            
            .summary-box {
              background: #f8fafc;
              padding: 20px;
              border: 1px solid #e5e7eb;
            }
            
            .summary-box h4 {
              color: #3b82f6;
              margin-bottom: 12px;
              font-size: 16px;
              font-weight: 600;
            }
            
            .summary-box p {
              margin: 8px 0;
              font-size: 14px;
              color: #374151;
            }
            
            .financial-summary {
              background: #f8fafc;
              padding: 24px;
              border: 2px solid #3b82f6;
            }
            
            .financial-summary h3 {
              color: #3b82f6;
              margin-bottom: 20px;
              font-size: 18px;
              font-weight: 600;
            }
            
            .financial-row {
              display: flex;
              justify-content: space-between;
              padding: 12px 0;
              border-bottom: 1px solid #e5e7eb;
              font-size: 14px;
            }
            
            .financial-row:last-child {
              border-bottom: 3px solid #3b82f6;
              font-weight: 700;
              font-size: 18px;
              color: #3b82f6;
              margin-top: 16px;
              padding-top: 16px;
            }
            
            .footer {
              background: #1f2937;
              color: white;
              padding: 32px;
              margin-top: 40px;
              text-align: center;
            }
            
            .footer p {
              margin: 8px 0;
              font-size: 14px;
              opacity: 0.8;
            }
            
            .footer .company {
              font-weight: 600;
              font-size: 16px;
              margin-bottom: 8px;
            }
            
            /* Zwiększ marginesy między sekcjami */
            .section + .section {
              margin-top: 20px;
            }
            
            /* Minimalna wysokość sekcji */
            .section {
              min-height: 100px;
            }
            
            .project-view {
              background: #f8fafc;
              border: 1px solid #e5e7eb;
              padding: 8px;
              margin: 8px 0;
              text-align: center;
            }
            
            .project-view h3 {
              color: #3b82f6;
              font-size: 14px;
              font-weight: 600;
              margin-bottom: 8px;
            }
            
            .project-view-placeholder {
              background: #e5e7eb;
              border: 2px dashed #9ca3af;
              padding: 20px;
              color: #6b7280;
              font-size: 12px;
              border-radius: 4px;
            }
          </style>
        </head>
        <body>
          <div class="container">
          <div data-pdf-section class="header">
            <div class="date">Data: ${new Date().toLocaleDateString('pl-PL')}</div>
            <h1>KOST-BET</h1>
            <div class="subtitle">Konfigurator Ogrodzeń</div>
          </div>
          
          <div data-pdf-section class="section">
            <h2>DANE KLIENTA</h2>
            <div class="fence-details">
              <div><strong>Imię i nazwisko:</strong> ${clientData.name || 'Nie podano'}</div>
              <div><strong>Email:</strong> ${clientData.email || 'Nie podano'}</div>
              <div><strong>Telefon:</strong> ${clientData.phone || 'Nie podano'}</div>
              <div><strong>Adres instalacji:</strong> ${clientData.address || 'Nie podano'}</div>
            </div>
          </div>
          
          <div data-pdf-section class="section">
            <h2>SZCZEGÓŁY OGRODZENIA</h2>
            <div class="fence-details">
              ${selectedSystem ? `<div><strong>System:</strong> ${selectedSystem.name}</div>` : ''}
              ${selectedSystem?.dimensions ? `<div><strong>Wymiary bloczka:</strong> ${selectedSystem.dimensions}</div>` : ''}
              ${selectedColor ? `<div><strong>Kolor:</strong> ${selectedColor}</div>` : ''}
              <div><strong>Całkowita długość:</strong> ${calculatedLength.toFixed(2)} m</div>
              <div><strong>Liczba elementów:</strong> ${elements.length}</div>
            </div>
          </div>
          
          <div data-pdf-section class="section">
            <h2>RZUT PROJEKTU Z GÓRY</h2>
            <div class="project-view">
              <div style="text-align: center; min-height: 150px; display: flex; flex-direction: column; justify-content: center;">
                <img src="${topViewImage}" alt="Rzut z góry ogrodzenia" style="max-width: 90%; max-height: 500px; height: auto; border: 1px solid #e5e7eb; margin: 0 auto;" />
                ${elements.length < 3 ? '<div style="margin-top: 10px; color: #6b7280; font-size: 11px;">Rzut z góry pokazuje układ elementów ogrodzenia</div>' : ''}
              </div>
            </div>
          </div>
          
          <div data-pdf-section class="section">
            <h2>SZCZEGÓŁOWA LISTA ELEMENTÓW</h2>
            <table class="elements-table">
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Wymiary</th>
                  <th>Cena</th>
                </tr>
              </thead>
              <tbody>
                ${elements.map(element => `
                  <tr>
                    <td>${getElementTypeName(element.type)}</td>
                    <td>${getElementDimensions(element)}</td>
                    <td>${element.type !== 'door' && element.type !== 'gate' ? `${element.price.toFixed(2)} zł` : '-'}</td>
                  </tr>
                `).join('')}
                ${elements.length < 5 ? Array(5 - elements.length).fill(0).map(() => `
                  <tr style="opacity: 0.3;">
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                  </tr>
                `).join('') : ''}
              </tbody>
            </table>
          </div>
          
          
          <div data-pdf-section class="section">
            <h2>PODSUMOWANIE FINANSOWE</h2>
            <div class="financial-summary">
              <h3>Kalkulacja kosztów</h3>
              <div class="financial-row">
                <span>Wartość netto:</span>
                <span>${(totalPrice / 1.23).toFixed(2)} zł</span>
              </div>
              <div class="financial-row">
                <span>VAT (23%):</span>
                <span>${(totalPrice - totalPrice / 1.23).toFixed(2)} zł</span>
              </div>
              <div class="financial-row">
                <span>WARTOŚĆ BRUTTO:</span>
                <span>${totalPrice.toFixed(2)} zł</span>
              </div>
            </div>
          </div>
          
          
          <!-- Stopka ukryta
          <div data-pdf-section class="footer">
            <p class="company">KOST-BET - Konfigurator Ogrodzeń</p>
            <p>www.kostbet.pl | tel: +48 XXX XXX XXX | email: info@kostbet.pl</p>
            <p>Wszystkie prawa zastrzeżone © 2025</p>
          </div>
          -->
          </div>
        </body>
        </html>
      `

      // 🎯 Renderuj HTML w ukrytym divie i konwertuj na PDF
      const fileName = `Oferta_KostBet_${new Date().toISOString().split('T')[0]}.pdf`
      
      // Utwórz tymczasowy kontener w głównym dokumencie
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'fixed'
      tempContainer.style.left = '-9999px'
      tempContainer.style.top = '0'
      tempContainer.style.width = '800px'
      tempContainer.style.zIndex = '-1'
      
      // Dodaj style do kontenera
      const styleEl = document.createElement('style')
      // Wyciągnij style z htmlContent (między <style> i </style>)
      const styleMatch = htmlContent.match(/<style>([\s\S]*?)<\/style>/)
      if (styleMatch) {
        // Zamień selektory na pdf-prefixed
        let cssText = styleMatch[1]
        // Nadpisz body i container style dla renderowania
        cssText += `
          body, .container { background: white !important; box-shadow: none !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
          .header, .section, .footer { page-break-inside: avoid; }
        `
        styleEl.textContent = cssText
      }
      document.head.appendChild(styleEl)
      
      // Wyciągnij body content z htmlContent
      const bodyMatch = htmlContent.match(/<body>([\s\S]*?)<\/body>/)
      if (bodyMatch) {
        tempContainer.innerHTML = bodyMatch[1]
      }
      document.body.appendChild(tempContainer)
      
      // Poczekaj na załadowanie obrazów
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // 🎯 Sekcja-po-sekcji: renderuj każdą sekcję jako osobny canvas
      const sections = tempContainer.querySelectorAll('[data-pdf-section]')
      
      const A4_WIDTH_MM = 210
      const A4_HEIGHT_MM = 297
      const MARGIN_MM = 10
      const CONTENT_WIDTH_MM = A4_WIDTH_MM - 2 * MARGIN_MM
      const CONTENT_HEIGHT_MM = A4_HEIGHT_MM - 2 * MARGIN_MM
      
      const pdf = new jsPDF('p', 'mm', 'a4')
      let currentY = MARGIN_MM
      let isFirstPage = true
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i] as HTMLElement
        
        // Renderuj sekcję jako canvas
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        })
        
        // Oblicz wymiary sekcji w mm
        const imgWidth = CONTENT_WIDTH_MM
        const imgHeight = (canvas.height * CONTENT_WIDTH_MM) / canvas.width
        
        // Sprawdź czy sekcja mieści się na aktualnej stronie
        if (currentY + imgHeight > A4_HEIGHT_MM - MARGIN_MM && !isFirstPage) {
          // Nie mieści się - nowa strona
          pdf.addPage()
          currentY = MARGIN_MM
        }
        
        // Jeśli sekcja jest wyższa niż jedna strona - podziel na chunki
        if (imgHeight > CONTENT_HEIGHT_MM) {
          let remainingHeight = imgHeight
          let sourceY = 0
          
          while (remainingHeight > 0) {
            const chunkHeight = Math.min(remainingHeight, CONTENT_HEIGHT_MM - (currentY - MARGIN_MM))
            
            if (chunkHeight < 10 && sourceY > 0) {
              // Za mało miejsca, nowa strona
              pdf.addPage()
              currentY = MARGIN_MM
              continue
            }
            
            // Oblicz clip z źródłowego obrazu
            const sourceChunkY = (sourceY / imgHeight) * canvas.height
            const sourceChunkH = (chunkHeight / imgHeight) * canvas.height
            
            // Utwórz canvas dla chunka
            const chunkCanvas = document.createElement('canvas')
            chunkCanvas.width = canvas.width
            chunkCanvas.height = sourceChunkH
            const ctx = chunkCanvas.getContext('2d')!
            ctx.drawImage(canvas, 0, sourceChunkY, canvas.width, sourceChunkH, 0, 0, canvas.width, sourceChunkH)
            
            const chunkData = chunkCanvas.toDataURL('image/jpeg', 0.95)
            pdf.addImage(chunkData, 'JPEG', MARGIN_MM, currentY, CONTENT_WIDTH_MM, chunkHeight)
            
            sourceY += chunkHeight
            remainingHeight -= chunkHeight
            currentY += chunkHeight
            
            if (remainingHeight > 0) {
              pdf.addPage()
              currentY = MARGIN_MM
            }
          }
        } else {
          // Sekcja mieści się - dodaj normalnie
          const imgData = canvas.toDataURL('image/jpeg', 0.95)
          pdf.addImage(imgData, 'JPEG', MARGIN_MM, currentY, imgWidth, imgHeight)
          currentY += imgHeight
        }
        
        isFirstPage = false
      }
      
      // Pobierz PDF
      pdf.save(fileName)
      
      // Wyczyść tymczasowy kontener i style
      document.body.removeChild(tempContainer)
      document.head.removeChild(styleEl)
      
      console.log('✅ Oferta PDF wygenerowana pomyślnie:', fileName)
      
    } catch (error) {
      console.error('❌ Błąd podczas generowania oferty PDF:', error)
      throw error
    }
  }
  
  
  return {
    generateOfferFile: generateOfferFile,
    showClientDataPopup,
    isPopupVisible,
    clientData,
    setClientData,
    closePopup
  }
}

// Komponent do generowania PDF
export const PDFGenerator: React.FC<{
  elements: FenceElement[]
  selectedSystem: FenceSystem | null
  selectedColor: string | null
  totalLength: number
  totalPrice: number
  onGeneratePDF?: () => void
  className?: string
}> = ({
  elements,
  selectedSystem,
  selectedColor,
  totalLength,
  totalPrice,
  onGeneratePDF,
  className = ''
}) => {
  
  const { 
    generateOfferFile, 
    showClientDataPopup, 
    isPopupVisible, 
    clientData, 
    setClientData, 
    closePopup 
  } = usePDFGenerator({
    elements,
    selectedSystem,
    selectedColor,
    totalLength,
    totalPrice
  })
  
  const handleGenerateOffer = async () => {
    showClientDataPopup()
  }

  const handleConfirmOffer = async () => {
    try {
      await generateOfferFile()
      closePopup()
      onGeneratePDF?.()
    } catch (error) {
      console.error('Błąd generowania oferty PDF:', error)
      alert('Wystąpił błąd podczas generowania oferty PDF. Spróbuj ponownie.')
    }
  }
  
  
  return (
    <>
    <div className={`flex flex-col gap-3 ${className}`}>
      <button
        onClick={handleGenerateOffer}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm touch-manipulation mobile-button"
          title="Pobierz szczegółową ofertę HTML z listą elementów i cenami"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
          <span>Generuj ofertę PDF</span>
      </button>
      </div>

      {/* Popup z danymi klienta */}
      {isPopupVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Dane klienta</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Imię i nazwisko *
                </label>
                <input
                  type="text"
                  value={clientData.name}
                  onChange={(e) => setClientData({...clientData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mobile-input"
                  placeholder="Wprowadź imię i nazwisko"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={clientData.email}
                  onChange={(e) => setClientData({...clientData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mobile-input"
                  placeholder="Wprowadź adres email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon *
                </label>
                <input
                  type="tel"
                  value={clientData.phone}
                  onChange={(e) => setClientData({...clientData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mobile-input"
                  placeholder="Wprowadź numer telefonu"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adres instalacji
                </label>
                <input
                  type="text"
                  value={clientData.address}
                  onChange={(e) => setClientData({...clientData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mobile-input"
                  placeholder="Wprowadź adres instalacji ogrodzenia"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closePopup}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors touch-manipulation mobile-button"
              >
                Anuluj
              </button>
      <button
                onClick={handleConfirmOffer}
                disabled={!clientData.name || !clientData.email || !clientData.phone}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors touch-manipulation mobile-button"
      >
                Generuj ofertę
      </button>
    </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PDFGenerator

